<template>
  <div>
    <div v-if="draggable">
      <draggable v-model="destinations" @change="onDraggableChange">
        <transition-group name="list" tag="div">
          <div
            class="inline-form-group list-group-item"
            v-for="(destination, index) in destinations"
            :key="index + 0"
          >
            <el-col
              :span="24"
              class="hidden-sm-and-up draggable-wrapper-border"
            >
              <el-row class="flex align-center">
                <el-col :span="19">
                  <dads-select
                    v-model="destination.type"
                    placeholder="Destination type"
                    filterable
                    @change="onDraggableDestTypeChange(index)"
                  >
                    <template slot="prefix">Select type</template>
                    <div v-for="item in filteredDraggableDestTypes" :key="item">
                      <el-option :label="destTypeLabels[item]" :value="item" />
                    </div>
                  </dads-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="4" class="text-right">
                  <el-button
                    class="table-action-button icon-blue"
                    icon="el-icon-rank"
                    style="width: 34px; height: 34px"
                  />
                </el-col>
              </el-row>
              <el-row class="flex align-center">
                <el-col :span="19">
                  <dads-select
                    v-model="destination.id"
                    filterable
                    placeholder="Select destination"
                    remote
                    :remote-method="
                      (query) =>
                        remoteSearchDraggableDestination(
                          query,
                          index,
                          destination.type
                        )
                    "
                    @change="onDstSelection"
                    @focus="
                      fetchDraggableDestinationOptions(index, destination.type)
                    "
                  >
                    <template slot="prefix">Destination</template>
                    <el-option
                      v-for="item in destination.options"
                      :key="item.id"
                      :label="getDstLabel(item, destination.type)"
                      :value="item.id"
                    />
                  </dads-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="4" class="text-right">
                  <el-button
                    class="table-action-button icon-red"
                    icon="el-icon-delete"
                    style="width: 34px; height: 34px"
                    @click="removeDestination(index)"
                  />
                </el-col>
              </el-row>
            </el-col>
            <!-- FOR SMALLER SCREENS -->
            <el-col
              :span="24"
              class="hidden-xs-only flex align-center draggable-wrapper-border"
            >
              <el-col :span="10">
                <dads-select
                  v-model="destination.type"
                  placeholder="Destination type"
                  filterable
                  @change="onDraggableDestTypeChange(index)"
                >
                  <template slot="prefix">Select type</template>
                  <div v-for="item in filteredDraggableDestTypes" :key="item">
                    <el-option :label="destTypeLabels[item]" :value="item" />
                  </div>
                </dads-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="10">
                <dads-select
                  v-model="destination.id"
                  filterable
                  placeholder="Select destination"
                  remote
                  :remote-method="
                    (query) =>
                      remoteSearchDraggableDestination(
                        query,
                        index,
                        destination.type
                      )
                  "
                  @change="onDstSelection"
                  @focus="fetchDraggableDestinationOptions(index)"
                >
                  <template slot="prefix">Destination</template>
                  <el-option
                    v-for="item in destination.options"
                    :key="item.id"
                    :label="getDstLabel(item, destination.type)"
                    :value="item.id"
                  ></el-option>
                </dads-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="4">
                <el-button
                  class="table-action-button icon-blue"
                  icon="el-icon-rank"
                  style="width: 34px; height: 34px"
                />
                <el-button
                  class="table-action-button icon-red"
                  icon="el-icon-delete"
                  style="width: 34px; height: 34px"
                  @click="removeDestination(index)"
                />
              </el-col>
            </el-col>
          </div>
        </transition-group>
      </draggable>
      <div class="inline-form-group">
        <el-button
          class="mv-10"
          type="default"
          icon="el-icon-circle-plus-outline"
          v-if="!terminalDestFound"
          :disabled="isAllowedToAddDestination || terminalDestFound"
          @click="addDestination"
        >
          Add a destination
        </el-button>
      </div>
    </div>
    <div v-else>
      <h2 v-if="!isKeyPress && showHeader" class="form-section-header">
        {{ title }}
      </h2>
      <div class="inline-form-group">
        <el-col :span="12">
          <dads-select
            v-model="destType"
            :placeholder="`${title} type`"
            :clearable="clearable"
            filterable
            @change="onSingleDestTypeChange"
          >
            <template slot="prefix">Destination type</template>

            <template v-if="!!customDestTypes && customDestTypes.length !== 0">
              <div v-for="(item, idx) in customDestTypes" :key="idx">
                <el-option :label="destTypeLabels[item]" :value="item" />
              </div>
            </template>
            <template v-else>
              <div v-for="(item, idx) in destTypes" :key="idx">
                <el-option :label="destTypeLabels[item]" :value="item" />
              </div>
            </template>
          </dads-select>
        </el-col>
        <el-col :span="1"></el-col>
        <el-col :span="11">
          <template slot="prefix">Destination</template>
          <dads-select
            v-model="destination.id"
            remote
            filterable
            :clearable="clearable"
            :allow-create="destType === 'CUSTOM' && isForwardingCalls"
            :placeholder="`${title}`"
            :remote-method="remoteSearchSingleDestOptions"
            @focus="fetchSingleDestinationOptions"
          >
            <template slot="prefix">Destination</template>
            <el-option
              v-for="(item, i) in destOptions"
              :key="i"
              :value="item.id"
              :label="getDstLabel(item, destType)"
            />
          </dads-select>
        </el-col>
      </div>
    </div>
  </div>
</template>

<script>
import feathersClient, { apiV2Host } from "../feathers/feathers-client";
import draggable from "vuedraggable";
import { debounce } from "lodash";
import DadsSelect from "@/components/select";

export default {
  name: "DestinationSelector",

  props: {
    maxDests: {
      default: 25
    },

    allowVMSameNumber: {
      default: false
    },

    isKeyPress: {
      type: Boolean,
      default: false
    },

    showHeader: {
      type: Boolean,
      required: false,
      default: true
    },

    title: {
      required: true,
      type: String
    },

    isForwardingCalls: {
      default: false,
      type: Boolean
    },

    clearFields: {
      required: true,
      type: Boolean
    },

    selectedDestination: {
      default: null
    },

    selectedDestinations: {
      default: null
    },

    customDestTypes: {
      default: null
    },

    draggable: {
      default: false
    },

    draggableDestTypes: {
      default: null
    },

    isTerminalDestSelector: {
      default: false
    },

    clearable: {
      type: Boolean,
      default: false
    },

    includePharmacyDests: {
      type: Boolean,
      default: false
    },

    includePharmacyAgents: {
      type: Boolean,
      default: false
    }
  },

  components: {
    draggable,
    DadsSelect
  },

  data() {
    return {
      // ⭐️ Draggable Destination Selector Data ⭐️ //
      destinations: [
        {
          options: [],
          id: null,
          type: ""
        }
      ],

      // ⭐️ General Destination Selector Data ⭐️ //
      destType: "",
      destOptions: [],

      destination: {
        id: null,
        type: "",
        options: []
      },

      destTypes: [
        "CUSTOM",
        "IVR",
        "VOICEMAIL",
        "PLAYBACK",
        "HUNTLIST",
        "EXT",
        "QUEUE",
        "SMS",
        "MUSICONHOLD",
        "CONDITION",
        "MEETME"
        // "DISA"
        // "MEDIAFILE",
      ],

      staticPharmacyAgentTypes: [
        "PIONEER_IVA",
        "BEST_RX_IVA",
        "LIBERTY_RX_IVA",
      ],

      staticPharmacyAllTypes: [
        "PIONEER_AGENT",
        "PIONEER_OUTBOUND_REFILL_AGENT",
        "PIONEER_SPANISH_AGENT",
        "PIONEER_REFILL",
        "PIONEER_SPANISH_REFILL",
        "PIONEER_STATUS",
        "PIONEER_SPANISH_STATUS",
        "BEST_RX_REFILL",
        "BEST_RX_STATUS",
        "BEST_RX_SPANISH_REFILL",
        "BEST_RX_SPANISH_STATUS",
        "LIBERTY_RX_REFILL",
        "LIBERTY_RX_STATUS",
        "LIBERTY_RX_SPANISH_REFILL",
        "LIBERTY_RX_SPANISH_STATUS",
        "PHARMACY_IVR_ENGLISH_REFILL",
        "PHARMACY_IVR_ENGLISH_STATUS",
        "PHARMACY_IVR_SPANISH_REFILL",
        "PHARMACY_IVR_SPANISH_STATUS",
      ],

      destTypeLabels: {
        SPECIAL: "Automation",
        CUSTOM: "Custom",
        IVR: "Go to a virtual receptionist",
        VOICEMAIL: "Go to a voicemail",
        PLAYBACK: "Play an audio file",
        HUNTLIST: "Ring a call group",
        EXT: "Ring an extension",
        QUEUE: "Ring a queue",
        SMS: "Send a text message",
        MUSICONHOLD: "Set hold music",
        CONDITION: "Time of day",
        MEETME: "Go to a conference room",
        DISABLEUNCONDITIONALDID: "Disable forwarding for number",
        ENABLEUNCONDITIONALDID: "Enable forwarding for number",
        LOGINQUEUE: "Login to queue",
        LOGOUTQUEUE: "Log out of queue",
        RERECORD: "Rerecord",
        SETUNCONDITIONALDID: "Set forwarding for number",
        TOGGLEPAUSEQUEUE: "Toggle queue pause",
        TOGGLEUNCONDITIONALDID: "Toggle forwarding for number",
        DISABLEUNCONDITIONALHUNTLIST: "Disable forwarding for call group",
        ENABLEUNCONDITIONALHUNTLIST: "Enable forwarding for call group",
        TOGGLEUNCONDITIONALHUNTLIST: "Toggle forwarding for call group",
        SETUNCONDITIONALHUNTLIST: "Set forwarding for call group",
        PIONEER_AGENT: "Pioneer IVR (EN)",
        PIONEER_IVA: "Pioneer Agent",
        PIONEER_SPANISH_AGENT: "Pioneer IVR (ES)",
        PIONEER_REFILL: "PioneerRx Refill (EN)",
        PIONEER_SPANISH_REFILL: "PioneerRx Refill (ES)",
        PIONEER_STATUS: "PioneerRx Status (EN)",
        PIONEER_SPANISH_STATUS: "PioneerRx Status (ES)",
        PIONEER_OUTBOUND_REFILL_AGENT: "Pioneer Outbound Refill IVR",
        BEST_RX_IVA: "BestRx Agent",
        BEST_RX_REFILL: "BestRx Refill (EN)",
        BEST_RX_STATUS: "BestRx Status (EN)",
        BEST_RX_SPANISH_REFILL: "BestRx Refill (ES)",
        BEST_RX_SPANISH_STATUS: "BestRx Status (ES)",
        LIBERTY_RX_IVA: "LibertyRx Agent",
        LIBERTY_RX_REFILL: "LibertyRx Refill (EN)",
        LIBERTY_RX_STATUS: "LibertyRx Status (EN)",
        LIBERTY_RX_SPANISH_REFILL: "LibertyRx Refill (ES)",
        LIBERTY_RX_SPANISH_STATUS: "LibertyRx Status (ES)",
        PHARMACY_IVR_ENGLISH_REFILL: "Pharmacy IVR Refill (EN)",
        PHARMACY_IVR_ENGLISH_STATUS: "Pharmacy IVR Status (EN)",
        PHARMACY_IVR_SPANISH_REFILL: "Pharmacy IVR Refill (ES)",
        PHARMACY_IVR_SPANISH_STATUS: "Pharmacy IVR Status (ES)"
      },

      dstLabelFields: {
        CONDITION: {
          field1: "name"
        },
        CUSTOM: {
          field1: "name"
          // field2: "number"
        },
        EXT: {
          field1: "name",
          field2: "number"
        },
        HUNTLIST: {
          field1: "name",
          field2: "number"
        },
        DISABLEUNCONDITIONALHUNTLIST: {
          field1: "name",
          field2: "number"
        },
        ENABLEUNCONDITIONALHUNTLIST: {
          field1: "name",
          field2: "number"
        },
        TOGGLEUNCONDITIONALHUNTLIST: {
          field1: "name",
          field2: "number"
        },
        SETUNCONDITIONALHUNTLIST: {
          field1: "name",
          field2: "number"
        },
        IVR: {
          field1: "name"
        },
        PLAYBACK: {
          field1: "name"
        },
        MUSICONHOLD: {
          field1: "name"
        },
        QUEUE: {
          field1: "name",
          field2: "number"
        },
        SMS: {
          field1: "number"
        },

        VOICEMAIL: {
          field1: "fullName",
          field2: "mailbox"
        },

        DISABLEUNCONDITIONALDID: {
          field1: "diCommentName",
          field2: "number"
        },
        ENABLEUNCONDITIONALDID: {
          field1: "diCommentName",
          field2: "number"
        },
        SETUNCONDITIONALDID: {
          field1: "diCommentName",
          field2: "number"
        },
        TOGGLEUNCONDITIONALDID: {
          field1: "diCommentName",
          field2: "number"
        },
        LOGINQUEUE: {
          field1: "name"
        },
        LOGOUTQUEUE: {
          field1: "name"
        },
        TOGGLEPAUSEQUEUE: {
          field1: "name"
        },
        SPECIAL: {
          field1: "name"
        },
        RERECORD: {
          field1: "name"
        },
        MEETME: {
          field1: "name"
        }
      },

      services: {
        CONDITION: "conditions",
        CUSTOM: "customs",
        DISA: "disas",
        DISABLEUNCONDITIONALDID: "dids",
        ENABLEUNCONDITIONALDID: "dids",
        SETUNCONDITIONALDID: "dids",
        SMS: "dids",
        TOGGLEUNCONDITIONALDID: "dids",
        EXT: "extensions",
        HUNTLIST: "huntlists",
        IVR: "ivrs",
        LOGINQUEUE: "queues",
        LOGOUTQUEUE: "queues",
        MEDIAFILE: "mediafiles",
        PLAYBACK: "mediafiles",
        RERECORD: "mediafiles",
        QUEUE: "queues",
        SPECIAL: "specials",
        TOGGLEPAUSEQUEUE: "queues",
        VOICEMAIL: "voicemails",
        MUSICONHOLD: "musiconholds",
        MEETME: "conference-bridges",
        DISABLEUNCONDITIONALHUNTLIST: "huntlists",
        ENABLEUNCONDITIONALHUNTLIST: "huntlists",
        TOGGLEUNCONDITIONALHUNTLIST: "huntlists",
        SETUNCONDITIONALHUNTLIST: "huntlists"
      },

      terminalDestTypes: [
        "EXT",
        "HUNTLIST",
        "CONDITION",
        "VOICEMAIL",
        "IVR",
        "QUEUE",
        "PIONEER_IVA",
        "BEST_RX_IVA",
        "LIBERTY_RX_IVA"
      ],
      terminalDestFound: false,

      dynamicPharmacyTypes: [],

      // ⭐️ REMOTE SEARCH FUNCTIONALITY ⭐️ //
      singleDestQuery: "",
      remoteSearchDraggableDestQuery: "",
      remoteSearchDraggableDestIndex: null,
      remoteSearchDraggableDestType: ""
    };
  },

  async created() {
    if (!this.includePharmacyDests && !this.includePharmacyAgents) return;

    if (this.includePharmacyDests) {
      this.dynamicPharmacyTypes.push(...this.staticPharmacyAllTypes);
    }

    if (this.includePharmacyAgents) {
      this.dynamicPharmacyTypes.push(...this.staticPharmacyAgentTypes);

      try {
        const res = await fetch(
          `${apiV2Host}/text-to-speech/supported-languages`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          }
        );
        const languages = await res.json();
        for (const lang of languages) {
          const key = `PHARMACY_AGENT_${lang.languageCode.toUpperCase()}`;
          this.destTypeLabels[key] = `Pharmacy Agent (${lang.language})`;
          this.dynamicPharmacyTypes.push(key);
        }
      } catch (e) {
        console.error("Failed to load supported languages for destinations", e);
      }
    }
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    filteredDraggableDestTypes() {
      return [...this.draggableDestTypes, ...this.dynamicPharmacyTypes];
    },

    destId() {
      return this.destination.id;
    },

    isAllowedToAddDestination() {
      if (this.destinations.length) {
        return (
          this.destinations.findIndex((dst) => !dst.id) !== -1 ||
          this.destinations.length === +this.maxDests
        );
      }
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    selectedDestination: {
      immediate: true,

      deep: true,

      async handler(val) {
        if (val && val.id === 0 && val.type) {
          this.destination.id = val.id;
          this.destType = val.type;

          const { options } = val;
          if (options && options.length) {
            this.destOptions = options;
          }
        }

        if (val && val.id && val.type) {
          this.destination.id = val.id;
          this.destType = val.type;

          const { options } = val;
          if (options && options.length) {
            this.destOptions = options;
          }
        }
      }
    },

    destId(val) {
      if (this.clearable && !val) {
        this.$emit("destinationCleared", {
          id: null,
          type: ""
        });
      }

      if (val || val === 0) {
        let isCustomDestCreated = false;

        if (this.destOptions.length) {
          isCustomDestCreated =
            this.destOptions.findIndex(
              (dst) => dst.id === this.destination.id
            ) === -1;
        }

        if (
          this.destType === "CUSTOM" &&
          this.isForwardingCalls &&
          isCustomDestCreated
        ) {
          this.$emit("customDestinationCreated", {
            type: this.destType,
            number: this.destination.id,
            id: null
          });
          return;
        }

        this.$emit("destinationSelected", {
          ...this.destination,
          type: this.destType
        });
      }
    },

    clearFields(val) {
      if (val) {
        this.destination = {
          type: "",
          id: null
        };

        this.destType = "";
        this.destOptions = [];

        this.destinations = [
          {
            options: [],
            id: null,
            type: ""
          }
        ];

        this.terminalDestFound = false;

        this.singleDestQuery = "";

        this.remoteSearchDraggableDestQuery = "";
        this.remoteSearchDraggableDestIndex = null;
        this.remoteSearchDraggableDestType = "";
      }
    },

    selectedDestinations: {
      immediate: true,

      async handler(val) {
        if (val && val.length) {
          this.destinations = [...val];
          // ⭐️ Check for terminal destination ⭐️ //

          if (this.isTerminalDestSelector) {
            for (let i = 0; i < val.length; i++) {
              if (this.terminalDestTypes.includes(val[i].type) || (val[i].type && val[i].type.startsWith("PHARMACY_AGENT_"))) {
                this.terminalDestFound = true;
                break;
              } else {
                this.terminalDestFound = false;
              }
            }
          }
        } else {
          this.destinations = [
            {
              options: [],
              id: null,
              type: ""
            }
          ];
        }
      }
    },

    // ⭐️ Remote Search FUNCTIONALITY ⭐️ //
    singleDestQuery(query) {
      if (query && query.length) {
        const remoteSearch = debounce(async () => {
          const $or = this.getSearchQuery(this.destType, query);

          if ($or.length > 0) {
            const currentDest = this.destOptions.find(
              ({ id }) => id === this.destination.id
            );

            const res = await this.getAvailableDestinations(this.destType, $or);
            this.destOptions = res.data || res;

            if (
              currentDest &&
              this.destOptions.findIndex(({ id }) => id === currentDest.id) ===
                -1
            ) {
              this.destOptions.push(currentDest);
            }
          }
        }, 250);
        remoteSearch();
      }
    },

    remoteSearchDraggableDestQuery(query) {
      if (query && query.length) {
        const remoteSearch = debounce(() => {
          const $or = this.getSearchQuery(
            this.remoteSearchDraggableDestType,
            this.remoteSearchDraggableDestQuery
          );

          const idx = this.remoteSearchDraggableDestIndex;
          const destType = this.remoteSearchDraggableDestType;

          if ($or.length > 0) {
            const currentDest = this.destinations[idx].options.find(
              ({ id }) => id === this.destinations[idx].id
            );

            this.getAvailableDestinations(destType, $or).then((res) => {
              this.destinations[idx].options = res.data || res;

              if (
                currentDest &&
                this.destinations[idx].options.findIndex(
                  ({ id }) => id === currentDest.id
                ) === -1
              ) {
                this.destinations[idx].options.push(currentDest);
              }
            });
          }
        }, 250);
        remoteSearch();
      }
    }
  },

  methods: {
    isPharmacyType(type) {
      return type.startsWith("PHARMACY_AGENT_") || type.startsWith("PHARMACY_IVR_");
    },

    onSingleDestTypeChange() {
      this.destOptions = [];
      this.destination.id = null;
    },

    async fetchSingleDestinationOptions() {
      const type = this.destType;
      if (type !== "") {
        this.destination.type = type;

        const res = await this.getAvailableDestinations(type);
        this.destOptions = res.data || res;
        this.$forceUpdate();
      }
    },

    onDraggableDestTypeChange(index) {
      this.destinations[index].options = [];
      this.destinations[index].id = null;
    },

    async fetchDraggableDestinationOptions(idx) {
      if (this.destinations[idx]) {
        this.destinations[idx].options = null;

        const destType = this.destinations[idx].type;

        if (destType) {
          const res = await this.getAvailableDestinations(destType);
          this.destinations[idx].options = res.data || res;
          this.$forceUpdate();
        }
      }
    },

    // ⭐️ REMOTE SEARCH METHODS ⭐️ //
    remoteSearchSingleDestOptions(query) {
      if (this.destType) {
        this.singleDestQuery = query;
      }
    },

    remoteSearchDraggableDestination(query, index, destType) {
      if (destType) {
        this.remoteSearchDraggableDestQuery = query;
        this.remoteSearchDraggableDestIndex = index;
        this.remoteSearchDraggableDestType = destType;
      }
    },

    getSearchQuery(destType, query) {
      if (
        destType.includes("PIONEER") ||
        destType.includes("BEST_RX") ||
        destType.includes("LIBERTY") ||
        destType.includes("QS1") ||
        this.isPharmacyType(destType)
      ) {
        return `&name=${query}`;
      }

      const fields = this.dstLabelFields[destType] || { field1: "name" };
      const { field1, field2 } = fields;
      const $or = [];
      if (field1) {
        const searchObj = {};
        searchObj[field1] = { $like: `%${query}%` };
        $or.push(searchObj);
      }
      if (field2) {
        const searchObj = {};
        searchObj[field2] = { $like: `%${query}%` };
        $or.push(searchObj);
      }
      return $or;
    },

    getDstLabel(dst, type) {
      const formatNumber = (num, includeDash = false) => {
        if (num) {
          return `${includeDash ? "-" : ""}(${num.substr(0, 3)})-${num.substr(
            3,
            3
          )}-${num.substr(6, 4)}`;
        }
        return "";
      };

      if (dst && type) {
        const fields = this.dstLabelFields[type] || { field1: "name" };
        const { field1, field2 } = fields;
        const field1Value = dst[field1];
        const field2Value = dst[field2];

        if (type === "CUSTOM") {
          if (field1Value === field2Value) {
            return `${formatNumber(field2Value)}`;
          }

          return `${field1Value ? field1Value : ""}${formatNumber(
            field2Value,
            true
          )}`;
        }

        if (
          type === "DISABLEUNCONDITIONALDID" ||
          type === "ENABLEUNCONDITIONALDID" ||
          type === "SETUNCONDITIONALDID" ||
          type === "TOGGLEUNCONDITIONALDID"
        ) {
          return formatNumber(field2Value, false);
        }

        if (type === "SMS") {
          return `${formatNumber(field1Value)}`;
        }

        return `${field1Value ? field1Value : ""}${
          field2Value ? " - " + field2Value : ""
        }`;
      }
    },

    async getAvailableDestinations(destType, $or = null) {
      if (
        destType.includes("PIONEER") ||
        destType.includes("BEST_RX") ||
        destType.includes("LIBERTY") ||
        this.isPharmacyType(destType)
      ) {
        let url = `${apiV2Host}/admin/pharmacy/agents?tenantId=${this.selectedTenant.id}&recordsPerPage=25&page=1`;

        if (destType.includes("PIONEER")) {
          url += `&type=PioneerRx`;
        }

        if (destType.includes("BEST_RX")) {
          url += `&type=BestRx`;
        }

        if (destType.includes("LIBERTY")) {
          url += `&type=LibertyRx`;
        }

        if ($or) {
          url += $or;
        }

        try {
          const res = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          });

          const data = await res.json();

          return data.records;
        } catch (err) {
          return [];
        }
      }

      // ⭐️ Get the sorting field ⭐️ //
      const { field1 } = this.dstLabelFields[destType] || { field1: "name" };

      const $sort = {};
      $sort[field1] = 1;

      let query = { tenantId: this.selectedTenant.id, $sort, $limit: 25 };

      if ($or) {
        query.$or = $or;
      }

      if (destType === "SPECIAL") {
        delete query.tenantId;
      }

      if (destType === "CONDITION") {
        query.type = { $ne: "AMD" };
        query.includeGlobals = false;
      }

      if (
        destType === "MEDIAFILE" ||
        destType === "PLAYBACK" ||
        destType === "RERECORD"
      ) {
        query.tenantId = { $in: [this.selectedTenant.id, 0] };
      }

      if (destType === "CUSTOM" && this.isForwardingCalls) {
        query.tenantId = this.selectedTenant.id;
        query.cu_ct_id = 1;
      }

      if (
        destType === "DISABLEUNCONDITIONALDID" ||
        destType === "ENABLEUNCONDITIONALDID" ||
        destType === "SETUNCONDITIONALDID" ||
        destType === "SMS" ||
        destType === "TOGGLEUNCONDITIONALDID"
      ) {
        query.tenantId = this.selectedTenant.id;
        query.allowEmergency = "";
      }

      if (destType === "MUSICONHOLD") {
        query.tenantId = { $in: [this.selectedTenant.id, 0] };
      }

      //allow selecting same number vm destination
      let results = [];
      if (destType === "VOICEMAIL" && this.allowVMSameNumber) {
        results.push({
          id: 0,
          fullName: "Same Number",
          mailbox: 0,
          tenantId: this.selectedTenant.id
        });
      }

      if (destType === "QUEUE") {
        const { userRole, adminQueues } = this.user;

        if (+userRole === 7) {
          query.id = { $in: adminQueues };
        }
      }

      return feathersClient
        .service(this.services[destType])
        .find({ query })
        .then((res) => {
          return [...results, ...res];
        });
    },

    // ⭐️ Draggable Destination Selector Methods ⭐️ //
    addDestination() {
      this.destinations.push({
        options: [],
        id: null,
        type: ""
      });

      this.$emit("destinationsSelected", this.destinations);
    },

    removeDestination(idx) {
      if (this.destinations.length === 1) {
        this.destinations = [
          {
            options: [],
            id: null,
            type: ""
          }
        ];
        this.$emit("destinationsSelected", this.destinations);
        return;
      }
      this.destinations = this.destinations.filter((row, index) => {
        return index !== idx;
      });

      this.$emit("destinationsSelected", this.destinations);
    },

    onDstSelection() {
      this.$emit("destinationsSelected", this.destinations);
    },

    onDraggableChange() {
      this.$emit("destinationsSelected", this.destinations);
    }
  }
};
</script>

<style scoped>
.list-enter-active,
.list-leave-active,
.list-move {
  transition: 500ms cubic-bezier(0.59, 0.12, 0.34, 0.95);
  transition-property: opacity, transform;
}

.list-enter {
  opacity: 0;
  transform: translateX(50px) scaleY(0.5);
}

.list-enter-to {
  opacity: 1;
  transform: translateX(0) scaleY(1);
}

.list-leave-active {
  position: absolute;
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
  transform-origin: center top;
}
</style>
